import type { components } from '@/api/generated-types';
import type { Locale } from '@/i18n/translations';
import {
  DonorError,
  guestRequest,
  locked,
  readStored,
  readStoredStrict,
  send,
  storeRequired,
  type ProtectedRequestOptions,
} from './http';
import { accountRequest, currentSession } from './session';
import { attemptSchema, checkoutSchema, draftSchema } from './response-schemas';
import { publicRequest } from '@/api/public-client';
import { z } from 'zod/mini';

export type Currency = 'RON' | 'EUR' | 'USD';
export type Quote = components['schemas']['BasketQuoteResponseDto'];
export type Checkout = components['schemas']['BasketCheckoutResponseDto'];
export type PaymentsConfig = components['schemas']['PaymentsConfigResponseDto'];
export type Policy = components['schemas']['CheckoutPolicyDto'];
export type Documents = components['schemas']['GivingDocumentsResponseDto'];
export type Project = components['schemas']['PublicProjectDetailResponseDto'];

export async function fetchPaymentsConfig(locale: Locale): Promise<PaymentsConfig> {
  const config = await send<PaymentsConfig | null>('/payments/config', locale);
  // TypeScript types do not validate the deployed API response. Refuse incomplete
  // web configuration before callers render or start browser verification.
  if (
    !config?.web ||
    typeof config.web.enabled !== 'boolean' ||
    (config.web.turnstileSiteKey !== null && typeof config.web.turnstileSiteKey !== 'string')
  )
    throw new DonorError(503, 'WEB_CHECKOUT_DISABLED');
  return config;
}

export interface Gift {
  projectId: string;
  organizationId: string;
  currency: Currency;
  amount: number;
  note?: string;
}
export interface Draft {
  revision: string;
  items: Gift[];
}
export interface Attempt {
  actorRef: string;
  mode: 'guest' | 'account';
  key: string;
  currency: Currency;
  draftKey: string;
  amount?: number;
  revision: string;
  quoteId: string;
  phase: 'CREATING' | 'CREATED';
  checkoutId?: string;
  createdAt: number;
}
const PENDING = 'giving.pending';
export const validId = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f-]{36}$/i.test(value);
export const money = (value: number, currency: string, locale: Locale): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 100);
/** Restore an editable amount without grouping or padding its decimal places. */
export const amountInputValue = (value: number, locale: Locale): string =>
  new Intl.NumberFormat(locale, {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value / 100);
export function parseAmount(value: string): number | null {
  const text = value.trim();
  if (!text) return 0;
  if (!/^\d{1,9}([.,]\d{1,2})?$/.test(text)) return null;
  const [whole, fractional = ''] = text.replace(',', '.').split('.');
  const amount = Number(whole) * 100 + Number(fractional.padEnd(2, '0'));
  return Number.isSafeInteger(amount) && amount <= 100_000_000 ? amount : null;
}
export function pendingAttempts(): Attempt[] {
  const parsed = z.array(attemptSchema).safeParse(readStoredStrict(PENDING, []));
  if (!parsed.success) throw new DonorError(0, 'STORAGE_REQUIRED');
  return parsed.data.map(({ checkoutId, amount, ...attempt }) => ({
    ...attempt,
    ...(checkoutId ? { checkoutId } : {}),
    ...(amount !== undefined ? { amount } : {}),
  }));
}

export function saveAttempt(attempt: Attempt): void {
  const validated = attemptSchema.safeParse(attempt);
  if (!validated.success) throw new DonorError(0, 'STORAGE_REQUIRED');
  storeRequired(PENDING, [
    ...pendingAttempts().filter((item) => item.key !== attempt.key),
    validated.data,
  ]);
  window.dispatchEvent(new Event('giving:pending'));
}
export function removeAttempt(key: string): void {
  storeRequired(
    PENDING,
    pendingAttempts().filter((item) => item.key !== key),
  );
  window.dispatchEvent(new Event('giving:pending'));
}
export function checkedCheckout(value: unknown, id?: string): Checkout {
  const parsed = checkoutSchema.safeParse(value);
  if (!parsed.success || (id && parsed.data.id !== id))
    throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
  // Financial and rendered fields have been decoded; unknown server extensions are retained.
  return parsed.data;
}

export const confirmed = (checkout: Checkout): boolean =>
  checkout.status === 'COMPLETED' && checkout.paymentStatus === 'SUCCEEDED';
export const fullyCancelled = (checkout: Checkout): boolean =>
  checkout.status === 'CANCELLED' &&
  checkout.paymentStatus === 'CANCELLED' &&
  !checkout.cancellationPending;
export function assertAttemptActor(
  attempt: Pick<Attempt, 'mode' | 'actorRef'>,
  epoch: number,
): void {
  const session = currentSession();
  if (
    session.epoch !== epoch ||
    session.phase === 'restoring' ||
    session.phase === 'unavailable' ||
    (attempt.mode === 'guest'
      ? Boolean(session.profile)
      : attempt.actorRef !== `user:${session.profile?.user.id ?? ''}`)
  )
    throw new DonorError(401, 'GIVING_ACTOR_CHANGED');
}
export async function actor(
  locale: Locale,
): Promise<{ actorRef: string; mode: 'guest' | 'account' }> {
  const session = currentSession();
  if (session.phase === 'restoring' || session.phase === 'unavailable')
    throw new DonorError(401, 'SIGN_IN_REQUIRED');
  if (session.profile) return { actorRef: `user:${session.profile.user.id}`, mode: 'account' };
  return locked('guest-session', async () => {
    await send('/giving-session', locale, { method: 'POST', guest: true });
    const probe = await send<{ actorRef: string }>('/giving-session', locale, { guest: true });
    if (!/^guest:[a-f0-9]{64}$/.test(probe.actorRef)) throw new DonorError(0, 'STORAGE_REQUIRED');
    return { actorRef: probe.actorRef, mode: 'guest' };
  });
}
export async function givingRequest<T>(
  path: string,
  locale: Locale,
  identity: { mode: 'guest' | 'account'; actorRef: string },
  options: ProtectedRequestOptions = {},
): Promise<T> {
  return identity.mode === 'guest'
    ? guestRequest<T>(path, locale, options)
    : accountRequest<T>(path, locale, options, identity.actorRef.slice(5));
}
async function recoverAttemptInLock(
  attempt: Attempt,
  locale: Locale,
  assertCurrent: () => void = () => undefined,
): Promise<Checkout | null> {
  assertCurrent();
  const result = await givingRequest<{
    outcome: 'RECOVERED' | 'RETIRED_UNCREATED';
    checkout?: Checkout;
  }>('/checkouts/recover', locale, attempt, {
    method: 'POST',
    key: attempt.key,
    body: { actorRef: attempt.actorRef },
  });
  assertCurrent();
  if (result.outcome === 'RETIRED_UNCREATED') {
    removeAttempt(attempt.key);
    return null;
  }
  if (!result.checkout) throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
  const checkout = checkedCheckout(result.checkout, attempt.checkoutId);
  if (
    checkout.currency !== attempt.currency ||
    (attempt.amount !== undefined && checkout.amount !== attempt.amount)
  )
    throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
  saveAttempt({ ...attempt, amount: checkout.amount, checkoutId: checkout.id, phase: 'CREATED' });
  return checkout;
}
export async function recoverAttempt(
  attempt: Attempt,
  locale: Locale,
  epoch?: number,
): Promise<Checkout | null> {
  return locked('payments', () =>
    recoverAttemptInLock(
      attempt,
      locale,
      epoch === undefined ? undefined : () => assertAttemptActor(attempt, epoch),
    ),
  );
}

/** Recover unknown operations before cancelling; never changes the donor's draft. */
export async function cancelAttempt(
  attempt: Attempt,
  locale: Locale,
  epoch: number,
): Promise<Checkout | null> {
  const assertCurrent = (): void => assertAttemptActor(attempt, epoch);
  return locked('payments', async () => {
    assertCurrent();
    const latest = pendingAttempts().find((pending) => pending.key === attempt.key);
    if (
      !latest ||
      latest.mode !== attempt.mode ||
      latest.actorRef !== attempt.actorRef ||
      latest.currency !== attempt.currency ||
      latest.quoteId !== attempt.quoteId ||
      latest.draftKey !== attempt.draftKey ||
      latest.revision !== attempt.revision ||
      (attempt.checkoutId && latest.checkoutId !== attempt.checkoutId)
    )
      throw new DonorError(409, 'CHECKOUT_IN_PROGRESS');
    const recovered = latest.checkoutId
      ? undefined
      : await recoverAttemptInLock(latest, locale, assertCurrent);
    assertCurrent();
    if (recovered === null) return null;
    const id = latest.checkoutId ?? recovered?.id;
    if (!id) throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
    const current = checkedCheckout(
      await givingRequest<Checkout>(`/checkouts/${id}`, locale, latest),
      id,
    );
    assertCurrent();
    if (
      current.currency !== latest.currency ||
      (latest.amount !== undefined && current.amount !== latest.amount) ||
      (recovered && current.amount !== recovered.amount)
    )
      throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
    if (
      fullyCancelled(current) ||
      current.cancellationPending ||
      current.status !== 'OPEN' ||
      current.paymentStatus !== 'PENDING'
    )
      return current;
    const cancelled = checkedCheckout(
      await givingRequest<Checkout>(`/checkouts/${id}/cancel`, locale, latest, { method: 'POST' }),
      id,
    );
    assertCurrent();
    if (cancelled.currency !== current.currency || cancelled.amount !== current.amount)
      throw new DonorError(0, 'INVALID_PAYMENT_RESPONSE');
    return cancelled;
  });
}
export async function publicProject(
  orgId: string,
  projectId: string,
  locale: Locale,
  signal?: AbortSignal,
): Promise<Project> {
  if (!validId(orgId) || !validId(projectId)) throw new DonorError(404, 'PROJECT_UNAVAILABLE');
  const project = await publicRequest<Project>(
    `/public/organizations/${orgId}/projects/${projectId}`,
    locale,
    { signal },
  );
  if (
    project.id !== projectId ||
    project.organizationId !== orgId ||
    project.status !== 'PUBLISHED'
  )
    throw new DonorError(404, 'PROJECT_UNAVAILABLE');
  return project;
}
export function loadDraft(key: string): Draft {
  const parsed = draftSchema.safeParse(readStored<unknown>(key, null));
  if (
    !parsed.success ||
    Date.now() - parsed.data.savedAt > 7 * 86400000 ||
    parsed.data.savedAt > Date.now() + 60_000
  )
    return { revision: crypto.randomUUID(), items: [] };
  return { revision: parsed.data.revision, items: parsed.data.items };
}

export function currentDraftRevision(key: string): string {
  return loadDraft(key).revision;
}
export function clearSubmittedDraft(attempt: Attempt): void {
  const draft = loadDraft(attempt.draftKey);
  if (draft.revision === attempt.revision) {
    storeRequired(attempt.draftKey, {
      revision: crypto.randomUUID(),
      savedAt: Date.now(),
      items: draft.items.filter((item) => item.currency !== attempt.currency),
    });
    window.dispatchEvent(new StorageEvent('storage', { key: attempt.draftKey }));
  }
}

export { useGiving } from './giving-context';
